import {
  Divider,
  AppBar,
  Box,
  Toolbar,
  Typography,
  Grid,
  Button,
  Link,
  CircularProgress,
  useTheme,
  useMediaQuery,
  Dialog,
  Backdrop,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import {useCallback, useRef} from 'react';
import mainLogo from '../../images/logo_black_letters_2.png';
import {useState, useEffect, useMemo, useContext} from 'react';
import {MultipleChoiceButtonGroup} from '../../components/buttons/multipleChoiceButtonGroup';
import {TriggerButton} from '../../components/sideBar/sideBarMenu';
import {mainLinearGradient} from '../../theme/gradients';
import {useDashboardApi} from '../../api/dashboard';
import dayjs from 'dayjs';
import {CustomSelect} from '../../components/dashboard/customSelect';
import {SubNavbar} from '../../components/dashboard/subNavbar';
import {handleFormattingByMetricKey, VitalsList} from '../../components/dashboard/vitalsList';
import {MetricsChart} from '../../components/dashboard/metricsChart';
import {useNavigate} from 'react-router-dom';
import {useResponsiveStyles} from '../../utils/responsiveness';
import {faArrowUpRightFromSquare, faChartColumn} from '@fortawesome/free-solid-svg-icons';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import {faList, faWrench, faTriangleExclamation} from '@fortawesome/pro-light-svg-icons';
import SymptomCheckerHistoryTabPanel from '../../components/tabPanels/symptomHistoryTabPanel';
import InformativeDialog from '../../components/dialogbox/informativeDialog';
import HorizontalDashIcon from '../../components/svgIcons/resultStrengthDashedIcon';
import MiscContext from '../../context/MiscContext';
import {useActivityClickApi} from '../../api/activityClick';
import {AnimationLoadingScreenWithProgressBar} from '../../components/loading/loadingScreen';
import {StreakHistory} from '../../components/dashboard/streaks';

export const customScrollbarStyles = {
  scrollbarWidth: 'thin', // For Firefox
  scrollbarColor: '#ccc #f0f0f0', // For Firefox

  '&::-webkit-scrollbar': {
    width: '12px', // Width of the vertical scrollbar
  },
  '&::-webkit-scrollbar-thumb': {
    backgroundColor: '#ccc',
    borderRadius: '6px', // Rounded corners
    '&:hover': {
      backgroundColor: '#aaa',
    },
  },
  '&::-webkit-scrollbar-track': {
    backgroundColor: '#f0f0f0',
    borderRadius: '6px', // Rounded corners
  },
};
const maxHeightStyles = {
  xs: '60vh',
  sm: '48vh',
  md: '65vh',
  lg: '56vh',
};

// Colours for the probabilities
const probColours = [
  {color: '#50BA47', opacity: 1},
  {color: '#A6E177', opacity: 1},
];

const disableVitalsChart = false;

/**
 * React functional component representing the Dashboard page.
 * @component
 * @returns {JSX.Element} The rendered DashboardPage component.
 */
export const DashboardPage = () => {
  const [days, setDays] = useState(0);
  const [hours, setHours] = useState(0);
  const [minutes, setMinutes] = useState(0);
  const [seconds, setSeconds] = useState(0);
  const [hasVitalsData, setHasVitalsData] = useState(true);
  const [canShowTimer, setCanShowTimer] = useState(false);
  const [isOpenResultStrength, setIsOpenResultStrenght] = useState(false);
  const [currentStreak, setCurrentStreak] = useState('');
  const [maxStreak, setMaxStreak] = useState('');
  const [informativeDialog, setInformativeDialog] = useState({
    title: '',
    subTitle: '',
    isOpen: false,
    width: 'sm',
  });
  const [resultsStrength, setResultsStrength] = useState({
    level: '',
    linesColourBlue: 0,
  });
  const {userFirstName} = useContext(MiscContext);

  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme => theme.breakpoints.down('sm'));
  const isSmallAndMediumScreen = useMediaQuery('(max-width:1180px)');

  const [totalVitalsHistory, setTotalVitalsHistory] = useState(undefined);
  const [symptomCheckerHistory, setSymptomCheckerHistory] = useState([]);
  const [mostRecentSymptomChecker, setMostRecentSymptomChecker] = useState({});
  const [pastSymptomChecker, setPastSymptomChecker] = useState([]);
  const [defaultSymptomDate, setDefaultSymptomDate] = useState({
    startDate: null,
    endDate: null,
  });
  const [isLoading, setIsLoading] = useState(true);
  const [responseReturned, setResponseReturned] = useState(false);
  const [filteredSymptomHistory, setFilteredHistory] = useState(symptomCheckerHistory);
  const chartContainerRef = useRef(null);

  const r = useResponsiveStyles(theme);
  const settingChartDimensions = useCallback(node => {
    if (!node ?? true) return;
    if (isChartContainerMounted) return;

    chartContainerRef.current = node;
    const height = node.getBoundingClientRect().height;
    const width = node.getBoundingClientRect().width;

    // 120px less because that's the width of the other 2 components in this container
    setRealChartHeight(height);
    setRealChartWidth(width);
    setIsChartContainerMounted(true);
  }, []);

  useEffect(() => {
    if (!chartContainerRef.current ?? true) return;
    settingChartDimensions(chartContainerRef.current);
  }, [window.innerHeight, window.innerWidth]);

  const [realChartHeight, setRealChartHeight] = useState(null);
  const [realChartWidth, setRealChartWidth] = useState(null);
  const [isChartContainerMounted, setIsChartContainerMounted] = useState(false);

  const mainComponentStyles = {
    my: 4,
    height: maxHeightStyles,
    backgroundColor: 'white',
    borderRadius: 4,
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between',
    boxShadow: isSmallScreen ? '0px 4px 6px rgba(0, 0, 0, 0.1)' : '',
  };

  const [defaultVital, setDefaultVital] = useState('overall_wellness');
  const [selectedVital, setSelectedVital] = useState(defaultVital);
  const [vitalsTimeFrame, setVitalsTimeFrame] = useState('month');
  const [vitalsChartType, setVitalsChartType] = useState(disableVitalsChart ? 'list' : 'trend');

  useEffect(() => {
    setSelectedVital(defaultVital);
  }, [defaultVital]);

  const currentDisplayedData = useMemo(() => {
    if (totalVitalsHistory === undefined) return undefined;
    if (selectedVital === '') return totalVitalsHistory[defaultVital];
    return totalVitalsHistory[selectedVital];
  }, [selectedVital, totalVitalsHistory]);
  const metricUnit = useMemo(() => {
    const CASES = {
      blood_pressure_systolic: 'mmHg',
      blood_pressure_diastolic: 'mmHg',
      heart_rate_change: 'ms',
      stress_index: '',
      pulse_rate: 'bpm',
      vascular_capacity: 'seconds',
      respiration: 'brpm',
      heart_disease_risk: 'percent',
      hypertension_risk: 'percent',
      hypercholesteremia_risk: 'percent',
      hypertriglyceridemia_risk: 'percent',
      hba1c: 'percent',
      blood_glucose: 'percent',
      diabetes_risk: 'percent',
      overall_wellness: 'percent',
      stroke_risk: 'percent',
      cardiovascular_risk: 'percent',
      cardiac_workload: 'dB',
    };
    return CASES[selectedVital] || '';
  }, [selectedVital]);

  const navigate = useNavigate();

  // apis
  const dashboardAPI = useDashboardApi();
  const activityClickApi = useActivityClickApi();

  const getTime = deadline => {
    const time = Date.parse(deadline) - Date.now();
    setDays(Math.floor(time / (1000 * 60 * 60 * 24)));
    setHours(Math.floor((time / (1000 * 60 * 60)) % 24));
    setMinutes(Math.floor((time / 1000 / 60) % 60));
    setSeconds(Math.floor((time / 1000) % 60));
  };
  useEffect(() => {
    //assuming we are getting the deadline from an endpoint
    const deadline = '2023-07-28T23:59';
    const time = Date.parse(deadline) - Date.now();

    if (time < 0) {
      return;
    }

    setCanShowTimer(true);
    const countdown = setInterval(() => {
      getTime(deadline);
    }, 1000);
    return () => clearInterval(countdown);
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      //Get streaks
      const streak = await dashboardAPI.getStreak();
      if (streak.status === 200) {
        setCurrentStreak(streak.data.currentStreak);
        setMaxStreak(streak.data.maxStreak);
      }
      //get symptom checker list of conditions
      const symptomHistory = await dashboardAPI.getSymptomCheckerHistoryList();

      if (symptomHistory.status === 200) {
        symptomHistory.data.forEach(item => {
          item.symptom_conditions.sort((a, b) => b.probability - a.probability);
        });
        setSymptomCheckerHistory(symptomHistory.data);

        const sortedSymptomCheckerData = [...symptomHistory.data];
        if (sortedSymptomCheckerData.length > 0) {
          //Handles the default date when dashboard is initialized
          const mostRecentData = sortedSymptomCheckerData[0];

          //sets the default recent symptom checker data before selecting a date range
          // Convert start and end dates to dayjs objects
          const startDate = dayjs(mostRecentData.created_time).subtract(1, 'day');
          const endDate = dayjs(mostRecentData.created_time).add(1, 'day');

          setDefaultSymptomDate({
            startDate: startDate,
            endDate: endDate,
          });

          // Filter the data based on the selected date range
          const filteredData = sortedSymptomCheckerData.filter(condition => {
            const createDate = dayjs(condition.created_time);
            return createDate.isAfter(startDate) && createDate.isBefore(endDate);
          });

          setFilteredHistory(filteredData);
          setMostRecentSymptomChecker(sortedSymptomCheckerData.shift());
        }

        setPastSymptomChecker(sortedSymptomCheckerData);
      }

      const vitalsHistoryReq = await dashboardAPI.getVitalsHistoryList();
      if (vitalsHistoryReq.status !== 200) return;
      const vitalsHistory = vitalsHistoryReq.data;
      // delete unneded properties, is important that these 2 lines of code are before the keys declaration
      delete vitalsHistory['face_skin_age'];
      delete vitalsHistory['body_mass_index'];
      const keys = Object.keys(vitalsHistoryReq.data);
      keys.forEach(vital => {
        if (vitalsHistory[vital].length === 0) {
          delete vitalsHistory[vital];
        } else {
          vitalsHistory[vital] = vitalsHistory[vital].map(val => {
            const yVal = handleFormattingByMetricKey(val.value, vital);
            return {x: new Date(val.created_time), y: yVal};
          });
        }
      });
      let theDefaultVital = vitalsHistory[defaultVital];
      // if the default vital doesn't have any values, it will take another random metric as the default vital
      if (!vitalsHistory.hasOwnProperty(defaultVital)) {
        theDefaultVital = vitalsHistory[keys[0]];
        setDefaultVital(keys[0]);
      }

      if (Object.keys(vitalsHistory).length === 0) {
        setHasVitalsData(false);
        return;
      }
      if (theDefaultVital.length === 1) {
        setVitalsChartType('list');
      }

      setTotalVitalsHistory(vitalsHistory);
    };
    fetchData();
    setResponseReturned(true);
    setIsLoading(false);
  }, []);

  const handleButtonsInfo = buttonName => {
    let buttonLabel = '';
    //Save in DB click activity of corresponding button
    if (buttonName === 'Treatment recommendations') {
      buttonLabel = 'treatments_button';
    } else if (buttonName === 'Condition Information') {
      buttonLabel = 'condition_info_button';
    } else {
      buttonLabel = 'find_doctor_button';
    }
    activityClickApi.postActivityClick(buttonLabel);

    setInformativeDialog({
      title: 'Coming soon',
      subTitle: `${buttonName} ${buttonName !== 'Treatment recommendations' ? 'is ' : 'are '} coming soon!`,
      isOpen: true,
      width: 'sm',
      onConfirm: () => {
        setInformativeDialog(prev => {
          return {...prev, isOpen: false};
        });
      },
    });
  };

  // Function to handle click and open the modal for result strenght in symptom checker on dashboard
  const closeModalResultStrenght = isClose => {
    setIsOpenResultStrenght(isClose);
  };

  const isVitalsHistoryDisabled = totalVitalsHistory === undefined || currentDisplayedData === undefined;

  return (isLoading && userFirstName === undefined) || userFirstName === '' ? (
    <AnimationLoadingScreenWithProgressBar title="Loading..." secondsToLoad={30} isAlmostComplete={responseReturned} />
  ) : (
    <Box sx={{maxWidth: '100vw', minHeight: '100vh'}}>
      <Box
        sx={{
          px: {xs: 1, sm: 3},
          backgroundColor: 'white',
          pb: 1,
          position: 'sticky',
          zIndex: 300,
          top: 0,
          boxSizing: 'border-box',
          boxShadow: '0px 14px 65px -40px rgba(0,0,0,0.69)',
        }}
      >
        <AppBar position="static" sx={{backgroundColor: 'white', pt: 1}} elevation={0}>
          <Toolbar sx={{justifyContent: 'space-between', backgroundColor: 'white'}}>
            <img src={mainLogo} height={60} />
            <TriggerButton />
          </Toolbar>
        </AppBar>
      </Box>
      <Box
        sx={{
          flexGrow: 1,
          backgroundColor: '#F5FCFC',
          my: 3,
          mt: 1,
          mx: {
            xs: 1,
            sm: 5,
          },
          borderRadius: 5,
          padding: 2,
          px: {xs: 0.5, sm: 3, md: 4},
        }}
      >
        <Grid container rowGap={2} columns={13} justifyContent="space-between" sx={{alignItems: 'stretch'}}>
          <Grid item xs={13}>
            <Typography variant="dashboardTitle" sx={{mx: isSmallScreen ? 1 : 0}}>
              Welcome back {userFirstName}!
            </Typography>
          </Grid>
          {!isSmallAndMediumScreen ? (
            <Grid
              container
              item
              xs={12}
              sm={12}
              md={12}
              lg={5.7}
              sx={{
                background: '#36c6c9',
                borderRadius: 5,
                p: 2,
                px: 2,
                pb: {
                  xs: 2,
                  lg: 3,
                },
                mt: 0,
                mx: isSmallScreen ? 1 : 0,
              }}
            >
              <Grid container item xs={12} justifyContent="space-between" alignItems="center" sx={{mb: -3, mt: -5}}>
                <Grid item xs={8}>
                  <Typography color="white" variant="dashboardTitle">
                    Symptom Checker
                  </Typography>
                </Grid>
                <Grid item xs={4} sx={{display: 'flex', justifyContent: 'flex-end'}}>
                  <Button
                    sx={{
                      minWidth: 'fit-content',
                      p: '0',
                    }}
                    onClick={() => navigate('/symptom-checker')}
                  >
                    <FontAwesomeIcon icon={faArrowUpRightFromSquare} size="lg" color={'white'} />
                  </Button>
                </Grid>
              </Grid>
              <Grid item xs={12} sx={{mt: -3.5}}>
                <Typography color="white" variant="body1">
                  Scan yourself for more than 2,000 conditions.
                </Typography>
              </Grid>
            </Grid>
          ) : (
            <Grid
              item
              container
              xs={13}
              sm={13}
              md={13}
              lg={5.7}
              sx={{
                background: '#36c6c9',
                borderRadius: 5,
                p: 2,
                px: 2,
                pb: {
                  xs: 4,
                  lg: 2,
                },
                my: 1,
                mx: isSmallScreen ? 1 : 0,
              }}
            >
              <Grid item xs={10} sm={8}>
                <Typography color="white" variant="dashboardTitle">
                  Symptom Checker
                </Typography>
              </Grid>
              <Grid item xs={2} sm={4} alignSelf="center" sx={{display: 'flex', justifyContent: 'end'}}>
                <Button
                  sx={{
                    minWidth: 'fit-content',
                    p: '0',
                    justifyContent: 'end',
                  }}
                >
                  <FontAwesomeIcon
                    icon={faArrowUpRightFromSquare}
                    size="lg"
                    onClick={() => navigate('/symptom-checker')}
                    color={'white'}
                  />
                </Button>
              </Grid>
              <Typography color="white" variant="body1">
                Scan yourself for more than 2,000 conditions.
              </Typography>
            </Grid>
          )}
          <Grid
            container
            item
            xs={12}
            sm={12}
            md={12}
            lg={5.7}
            sx={{
              background: '#36c6c9',
              borderRadius: 5,
              p: 2,
              px: 2,
              pb: {
                xs: 2,
                lg: 3,
              },
              mx: isSmallScreen ? 1 : 0,
            }}
          >
            <Grid item xs={10} sm={8} sx={{mt: isSmallAndMediumScreen ? 0 : 1.5}}>
              <Typography color="white" variant="dashboardTitle">
                Streaks
              </Typography>
            </Grid>
            <Grid item xs={6} sm={9} md={9} lg={9}>
              <Typography color="white" variant="body1">
                Complete a basic health scan every day to build your streak.
              </Typography>
            </Grid>
            <Grid item xs={6} sm={3} md={3} lg={3}>
              <StreakHistory currentStreak={currentStreak} maxStreak={maxStreak} />
            </Grid>
          </Grid>
          <Grid item container className="big1" xs={12} lg={5.7} flexWrap="wrap">
            <Grid
              item
              xs={12}
              className="small1"
              sx={{
                height: 'fit-content',
                display: 'flex',
                justifyContent: 'space-between',
                flexWrap: 'wrap',
                mx: 1,
              }}
            >
              <Typography variant="dashboardTitle" flexGrow={1}>
                Vitals History
              </Typography>
              <Box
                flexGrow={1}
                sx={{
                  maxWidth: {
                    xs: '45vw',
                    sm: undefined,
                    lg: '50%',
                  },
                }}
              >
                <CustomSelect
                  disabled={isVitalsHistoryDisabled}
                  options={totalVitalsHistory !== undefined ? Object.keys(totalVitalsHistory) : [defaultVital]}
                  value={!hasVitalsData ? '' : selectedVital}
                  label={!hasVitalsData ? '' : selectedVital}
                  onChange={e => {
                    setSelectedVital(e.target.value);
                  }}
                />
              </Box>
            </Grid>
            <Grid item xs={13} sm={12} md={12} className="medium1" sx={mainComponentStyles}>
              {vitalsChartType === 'trend' ? (
                <>
                  <Box
                    sx={{
                      width: {xs: '80%', md: '50%'},
                      py: 1,
                      height: 60,
                      mx: 'auto',
                      boxSizing: 'border-box',
                    }}
                  >
                    {!disableVitalsChart ? (
                      <MultipleChoiceButtonGroup
                        selected={vitalsTimeFrame}
                        disabled={isVitalsHistoryDisabled}
                        setSelected={setVitalsTimeFrame}
                        choices={[
                          {
                            value: 'day',
                            text: 'Day',
                          },
                          {
                            value: 'week',
                            text: 'Week',
                          },
                          {
                            value: 'month',
                            text: 'Month',
                          },
                          {
                            value: 'year',
                            text: 'Year',
                          },
                        ]}
                      />
                    ) : (
                      ''
                    )}
                  </Box>
                  {!disableVitalsChart && !isVitalsHistoryDisabled ? (
                    <Box
                      ref={settingChartDimensions}
                      sx={{
                        display: 'flex',
                        height: '80%',
                        boxSizing: 'border-box',
                      }}
                    >
                      <MetricsChart
                        key={selectedVital || defaultVital}
                        timeRange={vitalsTimeFrame}
                        setTimeRange={setVitalsTimeFrame}
                        metricUnit={metricUnit}
                        metricKey={selectedVital}
                        wholeData={currentDisplayedData}
                        chartHeight={realChartHeight}
                        chartWidth={realChartWidth}
                      />
                    </Box>
                  ) : (
                    <Box
                      sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'center',
                        alignItems: 'center',
                      }}
                    >
                      {hasVitalsData && isVitalsHistoryDisabled ? (
                        <>
                          <CircularProgress size={80} />
                          <Typography mt={1} variant={'h5'} sx={{color: theme.palette.primary.main}}>
                            Loading...
                          </Typography>
                        </>
                      ) : (
                        <>
                          <FontAwesomeIcon
                            icon={disableVitalsChart ? faWrench : faTriangleExclamation}
                            style={{fontSize: '50px', color: 'grey'}}
                          />
                          {disableVitalsChart ? (
                            <Typography color="grey" textAlign="center" mt={3}>
                              Working on it!
                            </Typography>
                          ) : (
                            <Typography color="grey" textAlign="center" mt={3}>
                              No vitals history
                              <br />
                              Click the symptom checker button above and complete the assessment to access vitals
                              history.
                            </Typography>
                          )}
                        </>
                      )}
                    </Box>
                  )}
                </>
              ) : (
                <Grid
                  container
                  sx={{
                    overflow: 'auto',
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: !isVitalsHistoryDisabled ? 'flex-start' : 'center',
                    alignItems: 'center',
                    pt: !isVitalsHistoryDisabled ? 2 : undefined,
                  }}
                >
                  {!isVitalsHistoryDisabled ? (
                    <VitalsList unit={metricUnit} data={currentDisplayedData} metricKey={selectedVital} />
                  ) : hasVitalsData ? (
                    <>
                      <CircularProgress size={80} />
                      <Typography mt={1} variant={'h5'} sx={{color: theme.palette.primary.main}}>
                        Loading...
                      </Typography>
                    </>
                  ) : (
                    <>
                      <FontAwesomeIcon icon={faTriangleExclamation} style={{fontSize: '50px', color: 'grey'}} />
                      <Typography color="grey" textAlign="center" mt={3} mx={4}>
                        No vitals history
                        <br />
                        Click the symptom checker button above and complete the assessment to access vitals history.
                      </Typography>
                    </>
                  )}
                </Grid>
              )}
              <Box
                sx={{
                  width: '100%',
                  display: 'flex',
                  justifyContent: 'center',
                  borderTop: '1px solid rgba(0, 0, 0, 0.12)',
                  py: 1,
                  boxSizing: 'border-box',
                  height: 60,
                }}
              >
                <SubNavbar
                  disabled={isVitalsHistoryDisabled}
                  options={[
                    {
                      text: 'Trend',
                      value: 'trend',
                      icon: <FontAwesomeIcon icon={faChartColumn} />,
                    },
                    {
                      text: 'List',
                      value: 'list',
                      icon: <FontAwesomeIcon icon={faList} />,
                    },
                  ]}
                  selected={vitalsChartType}
                  setSelected={setVitalsChartType}
                />
              </Box>
            </Grid>
          </Grid>
          <Grid item xs={13} sx={{display: {xs: 'block', sm: 'none'}}}>
            <Divider sx={{my: 2}} />
          </Grid>
          <Grid item container className="big2" xs={12} lg={5.7} flexWrap="wrap">
            <Grid
              item
              xs={12}
              className="small2"
              sx={{
                height: 'fit-content',
                display: 'flex',
                justifyContent: 'space-between',
                flexWrap: 'wrap',
                mx: 1,
              }}
            >
              <Typography variant="dashboardTitle" flexGrow={1}>
                Symptom Checker History
              </Typography>
            </Grid>
            <Grid item xs={13} sm={12} md={12} className="medium2" sx={mainComponentStyles}>
              <SymptomCheckerHistoryTabPanel
                pastSymptomCheckerHistory={pastSymptomChecker}
                mostRecentSymptomChecker={mostRecentSymptomChecker}
                probColours={probColours}
                handleButtonsInfo={handleButtonsInfo}
              />
            </Grid>
          </Grid>
          {canShowTimer ? (
            <>
              <Grid
                item
                xs={13}
                lg={7}
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'center',
                  alignItems: {
                    xs: 'center',
                    lg: 'start',
                  },
                }}
              >
                <Typography
                  variant="h6"
                  sx={{
                    fontWeight: 600,
                    textAlign: {xs: 'center', sm: 'left'},
                  }}
                >
                  Do you think a friend could benefit from our app?
                </Typography>
                <Typography
                  sx={{
                    fontWeight: 600,
                    textAlign: {xs: 'center', sm: 'left'},
                  }}
                >
                  <Link href="#" color="#3DA9F8">
                    Share with a friend
                  </Link>{' '}
                  for the next 7 days to get a free month premium
                </Typography>
              </Grid>
              <Grid
                item
                xs={13}
                lg={6}
                sx={{
                  display: 'flex',
                  justifyContent: {
                    xs: 'center',
                    lg: 'end',
                  },
                }}
              >
                <Box
                  sx={{
                    background: ` linear-gradient(white,white) padding-box,${mainLinearGradient} border-box`,
                    boxShadow: '0px 4px 4px 0px #00000040',
                    border: '0 px transparent',
                    borderRadius: 4,
                  }}
                >
                  <Typography
                    sx={{
                      background: mainLinearGradient,
                      WebkitBackgroundClip: 'text',
                      WebkitTextFillColor: 'transparent',
                      p: 2,
                      fontWeight: 400,
                      fontSize: 25,
                    }}
                  >
                    {days}d: {hours}h: {minutes}m: {seconds}s
                  </Typography>
                </Box>
              </Grid>
            </>
          ) : (
            ''
          )}
        </Grid>
      </Box>
      {/* Modal dialog box when user clicks  buttons on symptom checker */}
      <InformativeDialog informativeDialog={informativeDialog} setInformativeDialog={setInformativeDialog} />
      {/* Modal box when user clicks the question icon in results strenght */}
      <Dialog
        open={isOpenResultStrength}
        maxWidth="sm"
        closeAfterTransition
        BackdropComponent={Backdrop}
        BackdropProps={{
          timeout: 500,
        }}
        PaperProps={{
          sx: {
            borderRadius: '30px',
            position: 'absolute',
            mx: {
              xs: 2,
              md: 'auto',
            },
          },
        }}
        sx={{
          top: theme.spacing(5),
          pt: theme.spacing(2),
          pb: theme.spacing(1),
        }}
      >
        <DialogTitle sx={{paddingY: theme.spacing(2)}}>
          <Box sx={{display: 'flex', alignItems: 'flex-start'}}>
            <Typography variant="h6" sx={{fontWeight: 'bold'}}>
              Results Strength
            </Typography>
          </Box>
        </DialogTitle>
        <DialogContent
          sx={{
            paddingRight: theme.spacing(3),
          }}
        >
          <Grid container spacing={2} alignItems="flex-start">
            <Grid item xs={3}>
              <Typography
                variant="body2"
                style={{
                  textAlign: 'left',
                  color: '#00adb5',
                }}
              >
                {resultsStrength.level.toUpperCase()}
              </Typography>
              {resultsStrength.level.toUpperCase() === 'LOW' && (
                <Typography
                  variant="body2"
                  style={{
                    textAlign: 'left',
                    color: '#00adb5',
                    fontSize: '12px',
                  }}
                >
                  Add more symptoms to improve results.
                </Typography>
              )}
            </Grid>
            <Grid item xs={9}>
              <Typography variant="body2" sx={{mb: 2}}>
                The symptoms you entered are a {resultsStrength.level.toLowerCase()} match with the symptoms of this
                condition.
              </Typography>
            </Grid>
            <Grid item xs={3}>
              <Typography variant="body2" style={{textAlign: 'left'}}>
                Results Strength:{' '}
              </Typography>
            </Grid>
            <Grid item xs={5}>
              <Typography variant="body2" style={{textAlign: 'left'}}>
                <span style={{color: '#00adb5'}}>{resultsStrength.level.toUpperCase()}</span>
              </Typography>
              <HorizontalDashIcon
                totalLines={4}
                linesWithColorA={resultsStrength.linesColourBlue}
                sx={{width: '100%'}}
              />
            </Grid>
          </Grid>
          <Typography variant="subtitle1" sx={{fontWeight: 'bold'}}>
            Understanding Your Results
          </Typography>
          <Typography variant="body2">
            • Match strength shows how well the symptoms you entered match the symptoms of each condition. It's not the
            likelihood of having the condition.
          </Typography>
          <Typography variant="body2">
            • Just because a condition is listed here, doesn't mean that you have it. Some of the conditions on this
            list are less frequently diagnosed.
          </Typography>
          <Typography variant="body2">• Talk to your doctor to understand what your symptoms may mean.</Typography>
        </DialogContent>
        <DialogActions sx={{paddingBottom: theme.spacing(2)}}>
          <Button variant="text" onClick={() => closeModalResultStrenght(false)}>
            Close
          </Button>
        </DialogActions>
      </Dialog>
      {/* Modal dialog box when user clicks  buttons on symptom checker */}
      <InformativeDialog informativeDialog={informativeDialog} setInformativeDialog={setInformativeDialog} />
    </Box>
  );
};
